'use client';

import { getUtmSuperProperties } from '@monorepo/client-common';
import {
  AdminTokenHeaderKey,
  AdminTokenKey,
  IDP_PROVIDER,
  IdpProviderKey,
} from '@monorepo/types';
import { getApiBaseUrl } from './api-config';
import { getDescopeSessionToken } from './descope-utils';

// Get admin token from sessionStorage or URL. When read from URL, removes it from the
// address bar and stores in sessionStorage to prevent leaking in telemetry (e.g. first_seen_url).
const getAdminToken = (): string | undefined => {
  if (typeof window === 'undefined') return undefined;
  const fromStorage = sessionStorage.getItem(AdminTokenKey);
  if (fromStorage) return fromStorage;
  const url = new URL(window.location.href);
  const token = url.searchParams.get(AdminTokenKey) || undefined;
  if (token) {
    sessionStorage.setItem(AdminTokenKey, token);
    url.searchParams.delete(AdminTokenKey);
    window.history.replaceState(null, '', url.toString());
  }
  return token;
};

const apiFetch = async (endpoint: string, options: RequestInit = {}) => {
  const baseUrl = getApiBaseUrl();
  const url = new URL(endpoint, baseUrl).toString();

  // Build headers as a plain object to allow property assignment
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(Array.isArray(options.headers)
      ? Object.fromEntries(options.headers)
      : (options.headers as Record<string, string>) || {}),
  };

  // Add IDP provider header for backend strategy selection
  // This allows existing users to continue using their auth method even if feature flag changes
  let idpProvider: string | null = null;
  if (typeof window !== 'undefined') {
    idpProvider = localStorage.getItem(IdpProviderKey);
    if (idpProvider) {
      headers['x-idp-provider'] = idpProvider;
    }
  }

  // Add Descope session token to Authorization header only if IDP provider is Descope
  // This allows mobile/alternate-origin clients to authenticate when cookies don't work
  // The DS cookie is only present when Descope is enabled and user is authenticated
  if (idpProvider === IDP_PROVIDER.DESCOPE) {
    const descopeSessionToken = getDescopeSessionToken();
    if (descopeSessionToken) {
      headers['Authorization'] = `Bearer ${descopeSessionToken}`;
    }
  }

  // Add admin token header if present (for impersonation)
  if (typeof window !== 'undefined') {
    const adminToken = getAdminToken();
    if (adminToken) {
      headers[AdminTokenHeaderKey] = adminToken;
    }
  }

  // Add UTM headers for backend Mixpanel event tracking
  const utmProperties = getUtmSuperProperties();
  if (utmProperties.utm_source) {
    headers['X-UTM-Source'] = utmProperties.utm_source;
  }
  if (utmProperties.utm_medium) {
    headers['X-UTM-Medium'] = utmProperties.utm_medium;
  }
  if (utmProperties.utm_campaign) {
    headers['X-UTM-Campaign'] = utmProperties.utm_campaign;
  }

  const response = await fetch(url, {
    ...options,
    headers,
    credentials: 'include',
  });

  if (!response.ok) {
    if (response.status === 401) {
      throw new Error('Unauthorized: Please log in to access this resource');
    }

    throw new Error(
      `API call failed: ${response.status} ${response.statusText}`,
    );
  }

  const res = await response.json();
  return res.data;
};

export default apiFetch;
